import React, { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import TimezoneSelect from "../../layout/TimezoneSelect";
import { timezoneMappings } from "../../../data/timezones";
import { useSelector, useDispatch } from "react-redux";
import validator from "validator";
import { useFormik } from "formik";
import * as Yup from "yup";
import YupPassword from "yup-password";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Button,
  Card,
} from "@mui/material";
import NotarizedModal from "../../NotarizedModal";
import LottieAnimation from "../../../animation/LottieAnimation";
import ExpandableTimezone from "../../modals/ExpandableTimezone";
import { usStates } from "../../../data/counties";
import SessionWillExpireBox from "../../modals/SessionWillExpireBox";
import SessionForcedLogoutBox from "../../modals/SessionForcedLogoutBox";
import { useNavigate } from "react-router-dom";
import {
  logoutHandler,
  clearSessionExpiredLogout,
} from "../../../store/actions/sessionsActions";
import { authorityTypes, signerTitles } from "../../../data/ronData";
import RegSigneeMainContentComponent from "./RegSigneeMainContentComponent";
import { convertStateAbbreviationToFullName } from "../../../utils/stateUtils";

YupPassword(Yup);

const BUSINESS_SUFFIXES = [
  "llc",
  "inc",
  "corporation",
  "company",
  "corp",
  "co",
  "co.",
  "ltd",
  "llp",
  "lp",
  "plc",
  "holding",
  "holdings",
  "trust",
  "partnership",
  "limited",
  "partners",
  "associates",
  "association",
  "enterprises",
  "group",
  "properties",
  "realty",
  "trustee",
];

const PERSONAL_SIGNER_NAME_ERROR_MESSAGE =
  "Enter the personal name of the authorized signer, not an organization name.";

const normalizeBusinessToken = (value = "") =>
  String(value).trim().toLowerCase().replace(/[.,]/g, "");

const isBusinessNameValue = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return false;

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return false;

  const lastWord = normalizeBusinessToken(parts[parts.length - 1]);
  return BUSINESS_SUFFIXES.includes(lastWord);
};

const RegisterSignee = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const guestUser = useSelector((state) => state.guestSession);
  const sessionForcedLogout = useSelector(
    (state) => state.sessions.sessionForcedLogout,
  );
  const scrollableElementRef = useRef(null);

  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const largeTabletSize = useMediaQuery(theme.breakpoints.down("lg"));
  const [pageLoading, setPageLoading] = useState(true);
  const [propertyDetails, setPropertyDetails] = useState([]);
  const [propertyImage, setPropertyImage] = useState([]);
  const [ids, setIds] = useState();
  const [documentModal, setDocumentModal] = useState(false);
  const availableChecked = true;
  const [scheduleTimeLeft, setScheduleTimeLeft] = useState(60);
  const [startTimer] = useState(false);
  const [startInstantTimer] = useState(false);
  const [checkDateLoading] = useState(false);
  const [isAvailable] = useState(false);
  const [slots] = useState([]);
  const [instantTimeLeft, setInstantTimeLeft] = useState(60);
  const [checkInstantLoading] = useState(false);
  const [isInstantAvailable] = useState(false);
  const [instantError] = useState("");
  const [ownerTypes, setOwnerTypes] = useState({});
  const [fullOwners, setFullOwners] = useState([]);
  const [ownerships, setOwnerships] = useState([]);
  const [immediateRequested] = useState(false);

  const [submitError, setSubmitError] = useState("");

  const [signerType, setSignerType] = useState("ORGANIZATION");

  const [instantAccepted] = useState(false);

  const [tempDate] = useState(null);
  const [tempTime] = useState(null);

  const [typeInfo, setTypeInfo] = useState(false);
  const [titleInfo, setTitleInfo] = useState(false);
  const [infoText, setInfoText] = useState("");
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [titleIsFadingOut, setTitleIsFadingOut] = useState(false);

  const [userTimezone, setUserTimeZone] = useState("America/New_York");
  const initialValuesSet = useRef(false);
  const [open, setOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const signerNameEditedRef = useRef(false);
  const markSignerNameEdited = () => {
    signerNameEditedRef.current = true;
  };
  const [initialFormValues, setInitialFormValues] = useState({
    owners: [],
    ownerAddress: "",
    fullOwnerSelect: "",
    ownersSelect: [],
    name: "",
    signatoryTitle: "",
    authority: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    dob: null,
    email: "",
    phone: "",
    idType: "",
    scheduledDate: null,
  });
  const [copyOwners, setCopyOwners] = useState([]);
  const isFieldsDisabled = () =>
    (immediateRequested && startInstantTimer) ||
    instantAccepted ||
    checkInstantLoading;
  const selectedRow = {
    task_id: guestUser?.registerData?.task_id,
    cst_packageID: guestUser?.registerData?.cst_packageID,
  };
  const [willExpireSoon, setWillExpireSoon] = useState(false);

  const handleBeforeNavigation = (event) => {
    const confirmation = window.confirm(
      "If you navigate away from this page, your session will end, and you'll need to start over. Do you want to proceed?",
    );
    if (confirmation) {
      // If the user confirms, navigate them to the login page
      navigate("/login");
    } else {
      // If the user cancels, push the current state back to prevent navigation
      window.history.pushState(null, "", window.location.href);
      event.preventDefault();
    }
  };

  useEffect(() => {
    const handlePopState = (event) => handleBeforeNavigation(event);

    // Push the current state so the back button triggers `popstate`
    window.history.pushState(null, "", window.location.href);

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkWillExpire = () => {
      const isExpiring = sessionStorage.getItem("willExpire") === "true";
      setWillExpireSoon(isExpiring);
    };

    const handleExpireUpdate = () => {
      checkWillExpire();
    };

    // Add listener
    window.addEventListener("expireUpdate", handleExpireUpdate);

    // Initial check
    checkWillExpire();

    return () => {
      window.removeEventListener("expireUpdate", handleExpireUpdate);
    };
  }, []);

  useEffect(() => {
    if (tempDate && tempTime) {
      const dateObj = {
        day_of_month: tempDate.date(), // Get the day of the month
        month: tempDate.month() + 1, // Months are zero-indexed, add 1 for 1-12 format
        year: tempDate.year(),
        time: tempTime.format("HH:mm"), // Formats the time in 'HH:mm' format
      };

      // Convert tempDate and tempTime to the user's timezone
      const userDateTime = DateTime.fromObject(
        {
          year: tempDate.year(),
          month: tempDate.month() + 1, // Adjust for zero-indexed months
          day: tempDate.date(),
          hour: tempTime.hour(),
          minute: tempTime.minute(),
          second: tempTime.second(),
        },
        { zone: userTimezone },
      );

      // Check if the date and time is in the past
      if (userDateTime < DateTime.now().setZone(userTimezone)) {
      } else {
        formik.setFieldValue("scheduledDate", dateObj, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempDate, tempTime, userTimezone]);
  useEffect(() => {
    if (typeInfo) {
      // First timeout to start the fade-out effect after 4500ms
      const timeout = setTimeout(() => {
        setIsFadingOut(true); // Start fading out
        // Second timeout to hide the alert and reset isFadingOut after the fade-out duration
        const fadeOutTimeout = setTimeout(() => {
          setTypeInfo(false); // Hide alert after fade-out completes
          setIsFadingOut(false); // Reset fading state
        }, 1000); // This duration should match the CSS fade-out duration

        return () => clearTimeout(fadeOutTimeout);
      }, 3500); // Show the alert for 5 seconds minus fade duration

      return () => clearTimeout(timeout);
    } else if (titleInfo) {
      const titleTimeout = setTimeout(() => {
        setTitleIsFadingOut(true); // Start fading out
        // Second timeout to hide the alert and reset isFadingOut after the fade-out duration
        const fadeOutTitleTimeout = setTimeout(() => {
          setTitleInfo(false); // Hide alert after fade-out completes
          setTitleIsFadingOut(false); // Reset fading state
        }, 1000); // This duration should match the CSS fade-out duration

        return () => clearTimeout(fadeOutTitleTimeout);
      }, 3500); // Show the alert for 5 seconds minus fade duration

      return () => clearTimeout(titleTimeout);
    }
  }, [typeInfo, titleInfo]);

  const extractOwnersData = (overviewData) => {
    const owners = [];
    const ownersSelect = [];
    let signerName = "";
    let ownerAddress = "";
    let ownerPhone = "";

    overviewData?.forEach((item) => {
      if (
        item.label.startsWith("Owner") &&
        item.label !== "Owner Address" &&
        item.label !== "Ownership Type"
      ) {
        // Add to owners array
        owners.push(item);
        ownersSelect.push({ label: item.label, value: item.type });
      } else if (item.label === "Owner Address") {
        ownerAddress = item.value;
      } else if (item.label === "ownerPhone") {
        ownerPhone = item.value;
      }
    });

    for (const owner of owners) {
      if (owner.type === "PERSON") {
        signerName = owner.value.PERSON;
        setSignerType("PERSON");
        break;
      } else if (owner.type === "ORGANIZATION") {
        // For organizations, signer name should use the personal name if present
        signerName = owner.value.PERSON || "";
        setSignerType("ORGANIZATION");
        break;
      }
    }

    return {
      owners,
      ownersSelect,
      signerName,
      // firstPersonMiddleName,
      // firstPersonLastName,
      ownerAddress,
      ownerPhone,
    };
  };

  useEffect(() => {
    if (slots.length > 0 || isAvailable) {
      // Assuming `slots` is your state variable
      scrollToBottom();
    }
  }, [slots, isAvailable]);

  const scrollToBottom = () => {
    const scrollableElement = scrollableElementRef.current;
    if (scrollableElement) {
      scrollableElement.scrollTop = scrollableElement.scrollHeight;
    }
  };

  useEffect(() => {
    if (!initialValuesSet.current && guestUser?.registerData) {
      initialValuesSet.current = true;

      const { owners, signerName, ownerAddress, ownerPhone } =
        extractOwnersData(
          guestUser?.registerData?.overview_data?.affected_property,
        );
      let address = "";
      let city = "";
      let state = "";
      let zipCode = "";
      const ownerNameCommonField =
        guestUser?.registerData?.common_fields?.find(
          (commonField) => commonField.common_name === "ownerName",
        ) || {};
      const commonOwnerName = ownerNameCommonField.value?.trim() || "";
      const apiPersonalName =
        guestUser?.registerData?.owner?.personalName?.trim() || "";

      if (guestUser?.registerData?.ownerAddressParts) {
        address =
          guestUser?.registerData?.ownerAddressParts?.address1 ||
          guestUser?.registerData?.ownerAddressParts?.address;
        city = guestUser?.registerData?.ownerAddressParts?.city;
        state = convertStateAbbreviationToFullName(
          guestUser?.registerData?.ownerAddressParts?.state,
        );
        zipCode =
          guestUser?.registerData?.ownerAddressParts?.zip_code ||
          guestUser?.registerData?.ownerAddressParts?.zipCode;
      }
      const ownersList = guestUser?.registerData?.ownersList || [];
      const normalizedOwnersList = Array.isArray(ownersList)
        ? ownersList
        : [ownersList].filter(Boolean);
      const selectedOwnerName = normalizedOwnersList[0] || "";
      const selectedOwnerRecord = owners.find((owner) => {
        if (owner.type === "ORGANIZATION") {
          return owner?.value?.ORGANIZATION === selectedOwnerName;
        }

        if (owner.type === "PERSON") {
          return owner?.value?.PERSON === selectedOwnerName;
        }

        return false;
      });
      const selectedOwners = selectedOwnerRecord ? [selectedOwnerRecord] : [];
      const selectedOwnersSelect = selectedOwnerRecord
        ? [
            {
              label: selectedOwnerRecord.label,
              value: selectedOwnerRecord.type,
            },
          ]
        : [];
      const selectedSignerName = selectedOwnerRecord
        ? selectedOwnerRecord?.value?.PERSON || ""
        : signerName;

      if (selectedOwnerRecord?.type) {
        setSignerType(selectedOwnerRecord.type);
      }
      setCopyOwners([owners[0]]);
      setOwnerships(guestUser?.registerData?.ownerships);
      setFullOwners(normalizedOwnersList);
      const formValues = {
        ...initialFormValues,
        owners: selectedOwners,
        fullOwnerSelect: selectedOwnerName,
        ownersSelect: selectedOwnersSelect,
        name: guestUser?.registerData?.cst_packageID
          ? guestUser?.registerData?.owner?.personal?.full_name
          : commonOwnerName ||
            apiPersonalName ||
            selectedSignerName ||
            guestUser?.registerData?.owner?.personal?.full_name,
        email:
          guestUser?.registerData?.ownerEmail ||
          guestUser?.registerData?.owner?.reg_email,
        ownerAddress: guestUser?.registerData?.cst_packageID
          ? guestUser?.registerData?.document_location?.project_address
          : ownerAddress,
        address: guestUser?.registerData?.cst_packageID
          ? guestUser?.registerData?.document_location?.project_address
          : address,
        city: city,
        state: guestUser?.registerData?.cst_packageID
          ? convertStateAbbreviationToFullName(
              guestUser?.registerData?.document_location?.state,
            )
          : state,
        zipCode: zipCode,
        phone: ownerPhone || guestUser?.registerData?.owner?.reg_mobile,
      };

      setInitialFormValues(formValues);
      setPropertyDetails(guestUser?.registerData?.overview_data);

      setPropertyImage(guestUser?.registerData?.img);
      setIds(guestUser?.registerData?.id);

      setPageLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestUser?.registerData]);

  const createValidationSchema = (availableChecked, isInstantAvailable) => {
    const ownerSchema = Yup.object().shape({
      value: Yup.object()
        .shape({
          PERSON: Yup.string(),
          ORGANIZATION: Yup.string(),
        })
        .test(
          "type-dependent-validation",
          "Invalid type dependent data",
          function (value) {
            // const { type } = this.parent;
            if (signerType === "PERSON") {
              if (!value.PERSON) {
                return this.createError({
                  path: `${this.path}.PERSON`,
                  message: "Individual Name is required",
                });
              }
            } else if (signerType === "ORGANIZATION") {
              if (!value.ORGANIZATION) {
                return this.createError({
                  path: `${this.path}.ORGANIZATION`,
                  message: "Organization name is required",
                });
              }
            }
            return true;
          },
        ),
    });

    const schemaFields = {
      name: Yup.string()
        .min(3, "Name is too short")
        .required("Name is required")
        .test(
          "two-parts", // Name of the test
          "Please enter at least first and last name, each with at least 3 characters", // Error message
          (value) => {
            // Split the name into parts based on spaces
            const parts = value ? value.split(" ").filter((part) => part) : [];
            // Check that there are at least two parts
            if (parts.length < 2) {
              return false;
            }
            // Check the first and last parts for minimum length of 3 characters
            const firstName = parts[0];
            const lastName = parts[parts.length - 1];
            return firstName.length >= 3 && lastName.length >= 3;
          },
        )
        .test(
          "personal-signer-name",
          PERSONAL_SIGNER_NAME_ERROR_MESSAGE,
          (value) => {
            if (!value) return true;

            if (signerType === "ORGANIZATION" && isBusinessNameValue(value)) {
              return false;
            }

            return true;
          },
        ),
      signatoryTitle:
        signerType === "PERSON" || guestUser?.registerData?.cst_packageID
          ? Yup.string()
          : Yup.string()
              .min(3, "Signer Title is too short")
              .required("Signer Title is required"),
      authority:
        signerType === "PERSON" || guestUser?.registerData?.cst_packageID
          ? Yup.string()
          : Yup.string()
              .min(3, "Type of Authority is too short")
              .required("Type of Authority is required"),
      ownerAddress: guestUser?.registerData?.cst_packageID
        ? Yup.string()
        : Yup.string()
            .min(5, "Owner address is too short")
            .required("Owner address is required"),
      address: guestUser?.registerData?.cst_packageID
        ? Yup.string()
        : Yup.string()
            .min(3, "Address is too short")
            .required("Address is required"),
      city: guestUser?.registerData?.cst_packageID
        ? Yup.string()
        : Yup.string().min(3, "City is too short").required("City is required"),
      state: guestUser?.registerData?.cst_packageID
        ? Yup.string()
        : Yup.string()
            .min(3, "State is too short")
            .required("State is required"),
      zipCode: guestUser?.registerData?.cst_packageID
        ? Yup.string()
        : Yup.string()
            .required("Zip Code is required")
            .test("zipCode", "Invalid zip code", function (value) {
              return validator.isPostalCode(value, "US");
            }),
      owners: Yup.array().of(ownerSchema),
    };

    return Yup.object(schemaFields);
  };
  const formik = useFormik({
    key: ids,
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: createValidationSchema(
      availableChecked,
      isInstantAvailable,
    ),
    validateOnBlur: true, // Validate only when an input field is blurred
    validateOnChange: false,
    onSubmit: async (values, helpers) => {},
  });

  const formikRef = useRef(formik);
  formikRef.current = formik;

  useEffect(() => {
    if (signerNameEditedRef.current) {
      return;
    }

    const primaryOwner = formik?.values?.owners?.[0];
    if (!primaryOwner) {
      return;
    }

    const ownerNameCommonField =
      guestUser?.registerData?.common_fields?.find(
        (commonField) => commonField.common_name === "ownerName",
      ) || {};
    const commonOwnerName = ownerNameCommonField.value?.trim() || "";
    const apiPersonalName =
      guestUser?.registerData?.owner?.personalName?.trim() || "";
    const personalName =
      commonOwnerName || apiPersonalName || primaryOwner.value?.PERSON || "";
    if (personalName && formik.values.name !== personalName) {
      formik.setFieldValue("name", personalName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.owners,
    signerType,
    guestUser?.registerData?.owner?.personalName,
  ]);

  useEffect(() => {
    if (!formik?.values?.owners?.length) {
      formik.setFieldValue("owners", copyOwners);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.owners]);

  // Effect for the countdown timer
  useEffect(() => {
    let intervalId;

    if (startTimer && scheduleTimeLeft > 0) {
      intervalId = setInterval(() => {
        setScheduleTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [startTimer, scheduleTimeLeft]);

  useEffect(() => {
    let intervalIdInstant;

    if (startInstantTimer && instantTimeLeft > 0) {
      intervalIdInstant = setInterval(() => {
        setInstantTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => clearInterval(intervalIdInstant);
  }, [startInstantTimer, instantTimeLeft]);

  useEffect(() => {
    if (
      submitError !== "" &&
      submitError !==
        "** Warning ** You should not leave the page or refresh the page while we are assigning you a notary. The process will take less than a minute."
    ) {
      const interv = setInterval(() => {
        setSubmitError("");
      }, 5000);
      return () => clearInterval(interv);
    }
  }, [submitError]);

  const handleScheduleClick = async () => {
    if (isBusinessNameValue(formik.values.name)) {
      await formik.setFieldTouched("name", true, true);
      await formik.setFieldError("name", PERSONAL_SIGNER_NAME_ERROR_MESSAGE);
      setSubmitError(PERSONAL_SIGNER_NAME_ERROR_MESSAGE);
      return;
    }

    const errors = await formik.validateForm();

    if (!errors || Object.keys(errors).length === 0) {
      setOpen(true);
      return;
    }

    for (const [field] of Object.entries(errors)) {
      await formik.setFieldTouched(field, true, true);
    }

    setSubmitError(
      "Some fields are incomplete. Please review the form carefully before proceeding.",
    );
  };

  return (
    <>
      {pageLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            {tabletSize ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <img
                    style={{
                      width: "10rem",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/website_img/PermitRockstar.png"
                  />
                </Box>
                <LottieAnimation />
              </>
            ) : (
              <CircularProgress color="warning" size="10rem" />
            )}
          </Box>
        </>
      ) : (
        <>
          {guestUser?.registerData?.cst_packageID ? (
            <Box
              sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                px: 2,
                py: 4,
              }}
            >
              <Card
                sx={{
                  width: "100%",
                  maxWidth: "800px",
                  padding: 4,
                  borderRadius: 3,
                  boxShadow: 6,
                  backgroundColor: "#fff",
                }}
              >
                {/* ⬇️ insert your scrollable content like this */}
                <Box
                  sx={{
                    maxHeight: "90vh",
                    overflowY: "auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      pt: 1,
                      pb: 0,
                      maxWidth: "90rem",
                      ml: "auto",
                      mr: "auto",
                      backgroundColor: "#fff",
                      zIndex: 1,
                      width: "100%",
                      position: "relative",
                      boxShadow: "2px 2px 14px 1px #d3d3d3",
                    }}
                  >
                    <img
                      style={{
                        width: mobileSize
                          ? "90%"
                          : tabletSize
                            ? "8rem"
                            : "8rem",
                        maxWidth: mobileSize ? "4rem" : undefined,
                      }}
                      alt="Permit Rockstar Logo"
                      src="/img/website_img/PermitRockstar.png"
                    />
                    {!mobileSize && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: tabletSize ? 18 : 27,
                          right: tabletSize ? 10 : 20,
                        }}
                      >
                        <TimezoneSelect
                          options={timezoneMappings}
                          timezone={userTimezone}
                          setTimezone={setUserTimeZone}
                          changeLocal={true}
                          specificWidth={largeTabletSize ? "11rem" : undefined}
                          specialDisable={
                            isInstantAvailable &&
                            !checkInstantLoading &&
                            !instantAccepted
                          }
                          specialDisabled2={
                            formik.values.scheduledDate ||
                            slots.length !== 0 ||
                            (instantError !== "" &&
                              instantError !==
                                "No available notaries at this moment.") ||
                            checkDateLoading
                          }
                        />
                      </Box>
                    )}
                    <Box sx={{ px: 1 }}>
                      <Typography
                        color="primary"
                        variant={mobileSize ? "h6" : "h4"}
                        sx={{ mb: mobileSize ? 1 : 2, mt: 1 }}
                        align="center"
                      >
                        Schedule Your Remote Online Notarization (RON) Session
                      </Typography>

                      {mobileSize && (
                        <ExpandableTimezone
                          open={timezoneOpen}
                          setOpen={setTimezoneOpen}
                          timezoneMappings={timezoneMappings}
                          userTimezone={userTimezone}
                          setUserTimeZone={setUserTimeZone}
                          largeTabletSize={largeTabletSize}
                          isInstantAvailable={isInstantAvailable}
                          checkInstantLoading={checkInstantLoading}
                          instantAccepted={instantAccepted}
                          formik={formik}
                          slots={slots}
                          instantError={instantError}
                          checkDateLoading={checkDateLoading}
                        />
                      )}
                    </Box>
                  </Box>
                  <RegSigneeMainContentComponent
                    formik={formik}
                    guestUser={guestUser}
                    propertyDetails={propertyDetails}
                    setDocumentModal={setDocumentModal}
                    ownerTypes={ownerTypes}
                    setOwnerTypes={setOwnerTypes}
                    fullOwners={fullOwners}
                    copyOwners={copyOwners}
                    setCopyOwners={setCopyOwners}
                    ownerships={ownerships}
                    isFieldsDisabled={isFieldsDisabled}
                    setSignerType={setSignerType}
                    markSignerNameEdited={markSignerNameEdited}
                    mobileSize={mobileSize}
                    tabletSize={tabletSize}
                    largeTabletSize={largeTabletSize}
                    titleInfo={titleInfo}
                    typeInfo={typeInfo}
                    setTitleInfo={setTitleInfo}
                    setTypeInfo={setTypeInfo}
                    infoText={infoText}
                    setInfoText={setInfoText}
                    titleIsFadingOut={titleIsFadingOut}
                    isFadingOut={isFadingOut}
                    signerTitles={signerTitles}
                    authorityTypes={authorityTypes}
                    open={open}
                    setOpen={setOpen}
                    selectedRow={selectedRow}
                    userTimezone={userTimezone}
                    usStates={usStates}
                  />
                </Box>

                <Box
                  sx={{
                    px: mobileSize ? 1 : 2,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    pb: 2,
                    borderRadius: 2,
                    boxShadow: "2px 2px 14px 1px #d3d3d3",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      mb: 0,
                    }}
                  >
                    {submitError ? (
                      <Typography
                        sx={{ mt: 1, mb: 1 }}
                        fontWeight="bold"
                        align="center"
                        variant={tabletSize ? "body2" : "h6"}
                        color="error"
                      >
                        {submitError}
                      </Typography>
                    ) : (
                      <Button
                        onClick={handleScheduleClick}
                        color="success"
                        sx={{ width: "20rem" }}
                        size={mobileSize ? "small" : "large"}
                        variant="contained"
                      >
                        Schedule your session
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                overflow: "hidden", // Prevents scrolling outside the main box
                backgroundImage: tabletSize
                  ? undefined
                  : "url('/img/website_img/homeImg.jpeg')",
                backgroundRepeat: "repeat",
                justifyContent: "center",
                alignItems: tabletSize ? undefined : "center",
              }}
            >
              {sessionForcedLogout && (
                <SessionForcedLogoutBox
                  onNavigateToLogin={async () => {
                    try {
                      // Clear the sessionForcedLogout flag first
                      await dispatch(clearSessionExpiredLogout());

                      // Logout and clean up the session
                      if (currentSession) {
                        await dispatch(logoutHandler(currentSession));
                      }

                      // Navigate to login page
                      navigate("/login", { replace: true });
                    } catch (error) {
                      console.error("Error during logout:", error);
                      // Still navigate to login even if logout fails
                      navigate("/login", { replace: true });
                    }
                  }}
                />
              )}
              {/* Top Fixed Area */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  pt: 1,
                  pb: 0,
                  maxWidth: "90rem",
                  ml: "auto",
                  mr: "auto",
                  backgroundColor: "#fff",
                  zIndex: 1,
                  width: "100%",
                  position: "relative",
                  boxShadow: "2px 2px 14px 1px #d3d3d3",
                }}
              >
                <img
                  style={{
                    width: mobileSize ? "90%" : tabletSize ? "8rem" : "8rem",
                    maxWidth: mobileSize ? "4rem" : undefined,
                  }}
                  alt="Permit Rockstar Logo"
                  src="/img/website_img/PermitRockstar.png"
                />
                {!mobileSize && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: tabletSize ? 18 : 27,
                      right: tabletSize ? 10 : 20,
                    }}
                  >
                    <TimezoneSelect
                      options={timezoneMappings}
                      timezone={userTimezone}
                      setTimezone={setUserTimeZone}
                      changeLocal={true}
                      specificWidth={largeTabletSize ? "11rem" : undefined}
                      specialDisable={
                        isInstantAvailable &&
                        !checkInstantLoading &&
                        !instantAccepted
                      }
                      specialDisabled2={
                        formik.values.scheduledDate ||
                        slots.length !== 0 ||
                        (instantError !== "" &&
                          instantError !==
                            "No available notaries at this moment.") ||
                        checkDateLoading
                      }
                    />
                  </Box>
                )}
                <Box sx={{ px: 1 }}>
                  <Typography
                    color="primary"
                    variant={mobileSize ? "h6" : "h4"}
                    sx={{ mb: mobileSize ? 1 : 2, mt: 1 }}
                    align="center"
                  >
                    Schedule Your Remote Online Notarization (RON) Session
                  </Typography>

                  {mobileSize && (
                    <ExpandableTimezone
                      open={timezoneOpen}
                      setOpen={setTimezoneOpen}
                      timezoneMappings={timezoneMappings}
                      userTimezone={userTimezone}
                      setUserTimeZone={setUserTimeZone}
                      largeTabletSize={largeTabletSize}
                      isInstantAvailable={isInstantAvailable}
                      checkInstantLoading={checkInstantLoading}
                      instantAccepted={instantAccepted}
                      formik={formik}
                      slots={slots}
                      instantError={instantError}
                      checkDateLoading={checkDateLoading}
                    />
                  )}
                </Box>
              </Box>

              {/* Scrollable Content Area */}
              <RegSigneeMainContentComponent
                formik={formik}
                guestUser={guestUser}
                propertyDetails={propertyDetails}
                setDocumentModal={setDocumentModal}
                ownerTypes={ownerTypes}
                setOwnerTypes={setOwnerTypes}
                fullOwners={fullOwners}
                copyOwners={copyOwners}
                setCopyOwners={setCopyOwners}
                ownerships={ownerships}
                isFieldsDisabled={isFieldsDisabled}
                setSignerType={setSignerType}
                markSignerNameEdited={markSignerNameEdited}
                mobileSize={mobileSize}
                tabletSize={tabletSize}
                largeTabletSize={largeTabletSize}
                titleInfo={titleInfo}
                typeInfo={typeInfo}
                setTitleInfo={setTitleInfo}
                setTypeInfo={setTypeInfo}
                infoText={infoText}
                setInfoText={setInfoText}
                titleIsFadingOut={titleIsFadingOut}
                isFadingOut={isFadingOut}
                signerTitles={signerTitles}
                authorityTypes={authorityTypes}
                open={open}
                setOpen={setOpen}
                selectedRow={selectedRow}
                userTimezone={userTimezone}
                usStates={usStates}
              />

              {/* Bottom Fixed Area */}
              <Box
                sx={{
                  py: 0.5,
                  position: "fixed",
                  bgcolor: "#fff",
                  maxWidth: "90rem",
                  ml: "auto",
                  mr: "auto",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  opacity: 1,
                  zIndex: 1100, // Higher index to ensure it's above other content
                }}
              >
                <Box
                  sx={{
                    px: mobileSize ? 1 : 2,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    pb: 2,
                    boxShadow: "2px 2px 14px 1px #d3d3d3",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      mb: 0,
                    }}
                  >
                    {submitError ? (
                      <Typography
                        sx={{ mt: 1, mb: 1 }}
                        fontWeight="bold"
                        align="center"
                        variant={tabletSize ? "body2" : "h6"}
                        color="error"
                      >
                        {submitError}
                      </Typography>
                    ) : (
                      <Button
                        onClick={handleScheduleClick}
                        color="success"
                        sx={{ width: "20rem" }}
                        size={mobileSize ? "small" : "large"}
                        variant="contained"
                      >
                        Schedule your session
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          <NotarizedModal
            notarizedModal={documentModal}
            setNotarizedModal={setDocumentModal}
            pdfImg={propertyImage?.[0]}
            pdfImg2={propertyImage?.[1]}
            previewImg=""
            preview={false}
            largeTabletSize={largeTabletSize}
            mobileSize={mobileSize}
            tabletSize={tabletSize}
            type={"registerSignee"}
          />
          {currentSession && (
            <SessionWillExpireBox
              willExpireSoon={willExpireSoon}
              setWillExpireSoon={setWillExpireSoon}
            />
          )}
        </>
        //! ////////////////////////////////////////////////////////////
      )}
    </>
  );
};

export default RegisterSignee;
